import { cn } from '@/lib/utils';
import type {
  WorldCupBookmakerMarkets,
  WorldCupTableMarketRow,
  WorldCupTeam,
} from '../../hooks/use-world-cup-gamelines';
import type {
  WorldCupDataTableRow,
  WorldCupDataTableSection,
  WorldCupSportsbookColumn,
} from './types';

const TABLE_COLUMNS: WorldCupSportsbookColumn[] = [
  { id: 'betmgm', label: 'BetMGM' },
  { id: 'fanduel', label: 'FanDuel' },
  { id: 'draftkings', label: 'DraftKings' },
];

interface BuildMarketSectionOptions {
  showRowVolume?: boolean;
  showParticipant?: boolean;
  homeTeam?: WorldCupTeam;
  awayTeam?: WorldCupTeam;
}

export function buildColumns(): WorldCupSportsbookColumn[] {
  return TABLE_COLUMNS;
}

export function buildMarketSection(
  title: string,
  markets: WorldCupBookmakerMarkets,
  meta: Pick<WorldCupDataTableSection, 'icon_name' | 'icon_class_name' | 'tooltip'>,
  options: BuildMarketSectionOptions = {},
): WorldCupDataTableSection {
  return {
    title,
    volume: formatVolume(markets.meta.total_volume),
    ...meta,
    rows: markets.rows.map((row) =>
      buildMarketRow(row, ['Moneyline', 'Halftime Result'].includes(title), options),
    ),
  };
}

function buildMarketRow(
  row: WorldCupTableMarketRow,
  badgeColorful?: boolean,
  options: BuildMarketSectionOptions = {},
): WorldCupDataTableRow {
  return {
    label: row.outcome.replace('Exact Score:', ''),
    secondary_label: formatSecondaryLabel(row, options),
    volume: options.showRowVolume ? formatVolume(row.total_volume) : undefined,
    badge_class_name: badgeColorful
      ? cn({
          'text-[#A772FF]': row.participant_type === 'home',
          'text-[#F7931A]': row.participant_type === 'away',
        })
      : '',
    probability: formatProbability(row.polymarket),
    probability_class_name: badgeColorful
      ? cn({
          'text-[#A772FF]': row.participant_type === 'home',
          'text-[#F7931A]': row.participant_type === 'away',
        })
      : row.outcome.toLowerCase() === 'no'
        ? 'text-[#FF2D2D]'
        : 'text-[#14C784]',
    odds: TABLE_COLUMNS.map((column) => ({
      value: formatSportsbookPrice(getRowOdds(row, column.id)),
      percent_value: formatWinRatePercent(getRowWinRatePercent(row, column.id)),
    })),
  };
}

function getRowOdds(row: WorldCupTableMarketRow, columnId: string) {
  if (columnId === 'betmgm') return row.betmgm;
  if (columnId === 'fanduel') return row.fanduel;
  if (columnId === 'draftkings') return row.draftkings;

  return null;
}

function getRowWinRatePercent(row: WorldCupTableMarketRow, columnId: string) {
  if (columnId === 'betmgm') return row.betmgmWinRatePercent;
  if (columnId === 'fanduel') return row.fanduelWinRatePercent;
  if (columnId === 'draftkings') return row.draftkingsWinRatePercent;

  return null;
}

function formatSecondaryLabel(row: WorldCupTableMarketRow, options: BuildMarketSectionOptions) {
  if (options.showParticipant) {
    if (row.outcome.trim().toLowerCase() === 'exact score: any other score') return 'Other';
    return formatParticipantLabel(row.participant_type, options);
  }

  return undefined;
}

function formatParticipantLabel(
  participantType: string | null | undefined,
  options: BuildMarketSectionOptions,
) {
  if (participantType === 'home') return `${options.homeTeam?.name} Win`;
  if (participantType === 'away') return `${options.awayTeam?.name} Win`;
  if (participantType === 'draw') return 'Draw';

  return undefined;
}

function formatProbability(value?: number | null) {
  if (value == null) return '-';

  return `${(value * 100).toFixed(1).replace(/\.0$/, '')}%`;
}

function formatSportsbookPrice(value?: number | null) {
  if (value == null) return '-';
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);

  return value > 0 ? `+${formatted}` : formatted;
}

function formatWinRatePercent(value?: number | null) {
  if (value == null) return '-';

  return `${value.toFixed(2)}%`;
}

function formatVolume(value?: number | null) {
  if (value == null) return '$0';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;

  return `$${value.toFixed(0)}`;
}
